<template>
  <section
    class="content-section"
    :class="{'is-bg': bgImage, 'is-dark': isDark}"
  >
    <BgMedia
      v-if="bgImage"
      :media="getBackground()"
    />
    <slot name="background"></slot>
    <div class="content">
      <div class="main">
        <h3
          v-if="title"
          class="title-small"
          v-inview-animate:once="{name: 'fade-down', duration: 500, delay: 300}"
          v-html="title"
        ></h3>
        <div
          v-if="text"
          class="markdown-content"
          v-inview-animate:once="{name: 'fade-down', duration: 500, delay: 400}"
          v-html="markdownToHTML(text || '')"
        >
        </div>
        <slot></slot>
      </div>
      <div
        class="side"
        v-if="!bgImage"
      >
        <slot name="side"></slot>
        <div
          v-if="image"
          class="image"
        >
          <BgMedia :source="image" />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import BgMedia from '@/components/BgMedia.vue';

// Markdown
const md = require('markdown-it')({
  html: true,
  linkify: true,
  typographer: true,
});

export default {
  name: 'TrainingSectionContent',
  components: {
    BgMedia,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
    image: {
      type: Object,
      default: null,
    },
    imageMobile: {
      type: Object,
      default: null,
    },
    theme: {
      type: String,
      default: 'light',
      validator: (value) => ['light', 'dark', 'backgroundImage'].includes(value),
    },
  },
  computed: {
    bgImage() {
      return this.theme === 'backgroundImage';
    },
    isDark() {
      return this.theme === 'dark';
    },
  },
  methods: {
    markdownToHTML(content) {
      return md.render(content);
    },
    getBackground() {
      return {
        background: this.image,
        backgroundMobile: this.imageMobile,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.content-section {
  $b: &;
  position: relative;
  display: flex;
  justify-content: flex-start;
  width: 100%;

  @include screen("lg", min) {
    min-height: calc(var(--vh, 1vh) * 100);
  }

  @include screen("sm", max) {
    transition: min-height 100s ease;
  }

  :deep(.bg-media) {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: block;
    overflow: hidden;
    z-index: 1;
  }

  .content {
    position: relative;
    display: flex;
    align-items: flex-start;
    z-index: 2;
    width: 100%;
    margin: auto 0;

    @include screen("md", max) {
      flex-wrap: wrap;
      margin: 0;
    }

    @include screen("xxl", min) {
      justify-content: space-between;
    }
  }

  .main {
    display: block;
    box-sizing: border-box;

    @include wRule(
      width,
      (
        xl: 1092,
        lg: 806,
        md: 1024,
      )
    );

    @include wRule(
      padding,
      (
        xl: 178 90 178 483,
        lg: 186 45 186 320,
        md: 167 135 74 304,
        sm: 111 45 105,
      )
    );

    @include screen("xxl", min) {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      padding-top: 178px;
      padding-bottom: 178px;
    }

    :deep(.markdown-content) {
      @include wRule(
        max-width,
        (
          xl: 480,
          lg: 405,
          md: 550,
          sm: none,
        )
      );

      p {
        display: block;
        letter-spacing: -0.02em;
        font-weight: 400;
        line-height: 1.6;
        width: 100%;

        strong {
          font-weight: 700;
        }

        a {
          text-decoration: underline;
        }

        @include wRule(
          font-size,
          (
            xl: 20,
            lg: 18,
            md: 16,
            sm: 30,
          )
        );

        &:not(:last-child) {
          @include wRule(
            margin-bottom,
            (
              xl: 32,
              lg: 24,
              md: 18,
              sm: 30,
            )
          );
        }
      }

      ul {
        display: block;
        letter-spacing: -0.02em;
        font-weight: 400;
        line-height: 1.6;
        width: 100%;

        @include wRule(
          font-size,
          (
            xl: 20,
            lg: 18,
            md: 16,
            sm: 30,
          )
        );

        li {
          position: relative;
          display: block;
          box-sizing: border-box;

          @include wRule(
            padding-left,
            (
              xl: 72,
              lg: 54,
              md: 38,
              sm: 75,
            )
          );

          &::before {
            position: absolute;
            top: 0;
            left: 0;
            display: block;
            content: "";
            background: currentColor;
            margin-top: 0.7em;

            @include wRule(
              width,
              (
                xl: 20,
                lg: 15,
                md: 11,
                sm: 20,
              )
            );

            @include wRule(
              height,
              (
                xl: 3,
                lg: 2,
                md: 2,
                sm: 3,
              )
            );
          }

          &:not(:last-child) {
            @include wRule(
              margin-bottom,
              (
                xl: 16,
                lg: 15,
                md: 12,
                sm: 24,
              )
            );
          }
        }
      }

    }

    :deep(.markdown-content_big) {
      p {
        line-height: 1.34;

        @include wRule(
          font-size,
          (
            xl: 26,
            lg: 21,
            md: 18,
            sm: 32,
          )
        );

        &:not(:last-child) {
          @include wRule(
            margin-bottom,
            (
              xl: 32,
            )
          );
        }
      }
    }

    :deep(.title-small) {
      display: block;
      width: 100%;
      font-weight: 700;
      line-height: 1.2;
      letter-spacing: -0.02em;

      &:not(:last-child) {
        @include wRule(
          margin-bottom,
          (
            xl: 50,
            lg: 44,
            md: 34,
            sm: 44,
          )
        );
      }
    }
  }

  .side {
    position: relative;
    display: block;
    flex: 1;
    box-sizing: border-box;

    @include wRule(
      padding,
      (
        xl: 178 0 178,
        lg: 186 0 186,
        md: 0 0 92 304,
        sm: 0 0 120,
      )
    );

    @include screen("md", max) {
      flex: none;
      width: 100%;
    }

    @include screen("xxl", min) {
      padding-left: pxtovw(147, xl);
    }
  }

  :deep(.image) {
    position: relative;
    display: block;
    overflow: hidden;
    width: 100%;

    @include ratio(828, 535);

    @include screen("lg", max) {
      @include ratio(560, 380);
    }

    @include screen("md", max) {
      @include ratio(720, 335);
    }

    @include screen("sm", max) {
      @include ratio(828, 535);
    }

    .bg-media {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  &.is-bg {
    background-color: color(dark);
    color: color(basic-light);

    @include screen("lg", min) {
      .content {
        margin: 0;
        align-self: stretch;
      }

      .main {
        align-self: stretch;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
    }

    .main {
      background: rgba(0, 0, 0, 0.48);
      backdrop-filter: blur(11px);

      @include screen("md", max) {
        backdrop-filter: none;
      }
    }
  }

  &.is-dark {
    background-color: color(dark);
    color: color(basic-light);

    :deep(ul) {
      li {
        &::before {
          background-color: color(main);
        }
      }
    }
  }
}
</style>
